import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/kfmApi";

// App-wide trial lifecycle banner:
//   • active trial  -> "X days left in your free trial" (re-shows once per day; drives conversion)
//   • trial ended   -> gentle "you're now on the Free plan" (dismiss once)
// Hidden for admins/beta (uncapped) and paying tiers. Reuses GET /me/subscription.
export default function TrialBanner() {
  const [sub, setSub] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [starting, setStarting] = useState(false);
  const navigate = useNavigate();

  const startPro = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "pro_monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setStarting(false);
      navigate("/billing");   // fall back to the full plans page
    }
  };

  useEffect(() => {
    let alive = true;
    api.get("/me/subscription").then(({ data }) => { if (alive) setSub(data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!sub || hidden) return null;
  if (sub.uncapped) return null;          // admins / beta
  if (sub.tier && sub.tier !== "free") return null;  // paying members

  const trial = sub.trial || {};
  const active = !!trial.active;
  const daysLeft = trial.days_left;

  if (active) {
    const key = "owp_trial_active_dismissed";
    if (typeof window !== "undefined" && localStorage.getItem(key) === String(daysLeft)) return null;
    const urgent = daysLeft <= 2;
    return (
      <div
        data-testid="trial-banner-active"
        className={`flex items-center gap-3 px-4 py-2.5 text-sm ${urgent ? "bg-amber-500/10 text-amber-200 border-b border-amber-500/30" : "bg-[#10222A] text-slate-400 border-b border-[#00E5FF]/15"}`}
      >
        <Sparkles className="w-4 h-4 shrink-0" strokeWidth={1.75} />
        <span className="flex-1 leading-snug">
          <b>{urgent ? "Only " : ""}{daysLeft} {daysLeft === 1 ? "day" : "days"} left</b> in your free trial — every study tool is
          unmetered.{" "}
          <button
            data-testid="trial-banner-upgrade"
            onClick={() => navigate("/billing")}
            className="underline font-semibold whitespace-nowrap hover:opacity-80"
          >
            Keep it after the trial <ArrowRight className="inline w-3.5 h-3.5 -mt-0.5" strokeWidth={2} />
          </button>
        </span>
        <button
          aria-label="Dismiss"
          data-testid="trial-banner-dismiss"
          onClick={() => { try { localStorage.setItem(key, String(daysLeft)); } catch (e) {} setHidden(true); }}
          className="shrink-0 opacity-50 hover:opacity-100 transition-opacity"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    );
  }

  // Trial ended (free tier, not active). Every free account started a trial, so this is safe.
  const key = "owp_trial_ended_dismissed";
  if (typeof window !== "undefined" && localStorage.getItem(key) === "1") return null;
  return (
    <div
      data-testid="trial-banner-ended"
      className="flex items-center gap-3 px-4 py-2.5 text-sm bg-[#0B0F16] text-slate-300 border-b border-[#29344D]"
    >
      <Sparkles className="w-4 h-4 shrink-0 text-[#00E5FF]" strokeWidth={1.75} />
      <span className="flex-1 leading-snug">
        Your free trial has ended — you're now on the <b>Free plan</b> (15 credits a month). Reading, notes and
        history stay free forever.
      </span>
      <button
        data-testid="trial-banner-restart-pro"
        onClick={startPro}
        disabled={starting}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#00E5FF] text-[#07090E] text-xs font-semibold px-3.5 py-1.5 hover:bg-[#3d686d] transition-colors disabled:opacity-60"
      >
        {starting ? "Starting…" : <>Restart with Pro <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.25} /></>}
      </button>
      <button
        aria-label="Dismiss"
        data-testid="trial-banner-ended-dismiss"
        onClick={() => { try { localStorage.setItem(key, "1"); } catch (e) {} setHidden(true); }}
        className="shrink-0 opacity-50 hover:opacity-100 transition-opacity"
      >
        <X className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
}
